
import React, { Component, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './Button';

/** Após este tempo sem erro, a recuperação automática volta a valer na mesma aba. */
const HEALTHY_RESET_MS = 30_000;

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

// Class component para capturar erros de render
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, { hasError: boolean; error: Error | null }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  private healthyTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    this.healthyTimer = setTimeout(() => {
      if (this.state.hasError) return;
      try {
        sessionStorage.removeItem('eamb_chunk_reload');
      } catch {
        /* ignore */
      }
    }, HEALTHY_RESET_MS);
  }

  componentWillUnmount() {
    if (this.healthyTimer) clearTimeout(this.healthyTimer);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    try {
      Sentry.withScope((scope) => {
        scope.setTag('boundary', 'app');
        scope.setTag('staleBundle', String(isStaleBundleError(error)));
        scope.setContext('react', { componentStack: errorInfo.componentStack?.slice(0, 2000) || '' });
        Sentry.captureException(error);
      });
    } catch {
      /* ignore */
    }

    if (isStaleBundleError(error)) {
      void hardRecoverFromStaleBundle('auto');
      return;
    }

    console.error('🚨 [ErrorBoundary] Erro capturado:', {
      error: error.toString(),
      message: error.message,
      stack: error.stack?.slice(0, 500),
      componentStack: errorInfo.componentStack?.slice(0, 300),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent.slice(0, 100),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorScreen
          error={this.state.error}
          onRetry={() => {
            this.setState({ hasError: false, error: null });
            try {
              sessionStorage.removeItem('eamb_chunk_reload');
            } catch {
              /* ignore */
            }
            void hardRecoverFromStaleBundle('manual');
          }}
        />
      );
    }
    return this.props.children;
  }
}

/** HTML antigo em cache + chunk novo = OPS no cadastro. Força HTML fresco. */
async function hardRecoverFromStaleBundle(mode: 'auto' | 'manual'): Promise<void> {
  try {
    if (mode === 'auto' && sessionStorage.getItem('eamb_chunk_reload')) {
      return;
    }
    sessionStorage.setItem('eamb_chunk_reload', '1');
  } catch {
    /* ignore */
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => k.startsWith('eambulante-') || k.includes('workbox'))
          .map((k) => caches.delete(k))
      );
    }
  } catch {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
    }
  } catch {
    /* ignore */
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_eamb_r', String(Date.now()));
  window.location.replace(url.toString());
}

function isStaleBundleError(error: Error | null): boolean {
  const msg = `${error?.name || ''} ${error?.message || ''}`;
  return /ChunkLoadError|dynamically imported module|importing a module script failed|mime type|Failed to fetch|error loading/i.test(
    msg
  );
}

const ErrorScreen = ({ error, onRetry }: { error: Error | null; onRetry: () => void }) => {
  const isDev = import.meta.env.DEV;
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
      <div className="max-w-md w-full bg-white rounded-[3rem] p-10 shadow-premium border border-red-100 animate-in zoom-in-95">
        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
          <AlertTriangle size={40} />
        </div>
        <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter italic mb-4">Ops! Algo falhou.</h2>
        <p className="text-sm text-slate-600 font-medium leading-relaxed mb-2">
          Atualize a página para continuar. Se acabou de publicar uma nova versão, um refresh resolve na maioria dos casos.
        </p>
        <p className="text-[11px] text-slate-400 font-bold uppercase leading-relaxed tracking-widest mb-8">
          Seus dados estão seguros no servidor.
        </p>
        <div className="space-y-4">
          <Button
            onClick={onRetry}
            className="w-full py-5 bg-slate-900 text-white font-black rounded-2xl flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} /> Atualizar página
          </Button>
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              window.location.replace(`/?_eamb_r=${Date.now()}`);
            }}
            className="text-[10px] font-black text-red-400 uppercase tracking-widest hover:text-red-600 transition-colors"
          >
            Limpar dados locais e recomeçar
          </button>
        </div>
        {error && (
          <p className="mt-6 text-[10px] text-slate-400 font-mono break-all leading-snug">
            Código: {(error.name || 'Error').slice(0, 40)} · {(error.message || '').slice(0, isDev ? 600 : 140)}
          </p>
        )}
      </div>
    </div>
  );
};

export const AppErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>;
};
